import React, { useMemo, useRef } from 'react';

const FILE_ACCEPT = [
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.webp',
  '.avif',
  '.heic',
  '.heif',
  '.mp4',
  '.m4v',
  '.webm',
  '.ogg',
  '.ogv',
  '.mov',
  '.flv',
  '.3gp',
  '.3g2',
  '.avi',
  '.mkv',
].join(',');

export default function LocalOpsMediaPicker({
  mediaItems,
  coverIndex,
  onCoverChange,
  onRemove,
  onInvalidate,
  onAddFiles,
  error,
  warning,
  uploadPct,
  helperText,
}) {
  const inputRef = useRef(null);
  const hasMedia = mediaItems.length > 0;
  const coverHint = useMemo(() => {
    if (!hasMedia) return helperText || 'Add photos/videos (recommended)';
    return 'Tap a thumb to set cover';
  }, [hasMedia, helperText]);

  return (
    <div className="localops-section">
      <h4>Media</h4>
      <div className="localops-helper">{coverHint}</div>
      <div className="localops-media-actions">
        <button
          type="button"
          className="localops-btn localops-btn--ghost"
          onClick={() => inputRef.current?.click()}
        >
          Add media
        </button>
        <input
          ref={inputRef}
          type="file"
          accept={FILE_ACCEPT}
          multiple
          style={{ display: 'none' }}
          onChange={(e) => onAddFiles?.(e.target.files)}
        />
      </div>
      {uploadPct > 0 ? (
        <div className="localops-media-progress" style={{ marginTop: 8 }}>
          <span style={{ width: `${uploadPct}%` }} />
        </div>
      ) : null}
      {error ? <div className="localops-error">{error}</div> : null}
      {warning ? (
        <div className="localops-helper" style={{ color: '#f59e0b', marginTop: 6 }}>
          {warning}
        </div>
      ) : null}
      {hasMedia ? (
        <div className="localops-media-grid" style={{ marginTop: 10 }}>
          {mediaItems.map((item, idx) => (
            <div
              key={item.preview || item.url || idx}
              className={`localops-media-thumb ${
                idx === coverIndex ? 'is-cover' : ''
              }`}
              role="button"
              tabIndex={0}
              onClick={() => onCoverChange?.(idx)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onCoverChange?.(idx);
                }
              }}
              aria-label={`Set cover ${idx + 1}`}
            >
              {item.placeholder ? (
                <div className="localops-media-thumb__placeholder">
                  {item.label || 'Draft preview expired.'}
                </div>
              ) : item.isVideo ? (
                item.preview ? (
                  <video
                    src={item.preview}
                    muted
                    playsInline
                    preload="metadata"
                    onError={() => onInvalidate?.(idx)}
                  />
                ) : (
                  <div className="localops-media-thumb__placeholder">Preview unavailable</div>
                )
              ) : item.preview ? (
                <img
                  src={item.preview}
                  alt=""
                  onError={() => onInvalidate?.(idx)}
                />
              ) : (
                <div className="localops-media-thumb__placeholder">Preview unavailable</div>
              )}
              <span className="localops-media-thumb__badge">
                {item.isVideo ? 'VIDEO' : 'IMAGE'}
              </span>
              {item.preview?.startsWith('blob:') ? (
                <span
                  className="localops-media-thumb__badge"
                  style={{
                    top: 26,
                    background: '#e0f2fe',
                    color: '#0b1220',
                  }}
                >
                  Draft preview
                </span>
              ) : null}
              {item.needsReattach ||
              (!item.file && item.preview && !item.persisted) ? (
                <span
                  className="localops-media-thumb__badge"
                  style={{
                    top: 26,
                    background: '#f59e0b',
                    color: '#0b1220',
                  }}
                >
                  Reattach to publish
                </span>
              ) : null}
              <button
                type="button"
                className="localops-media-thumb__remove"
                onClick={(event) => {
                  event.stopPropagation();
                  onRemove?.(idx);
                }}
                aria-label="Remove media"
              >
                x
              </button>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
